import React, { useState } from "react";
import { MapPin, ChevronDown, ChevronUp, Calendar, TrendingUp } from "lucide-react";
import { experiences, companyLogos } from "../mock";

const companyAccents = {
  "GoodScore":    { color: "#10B981", bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.25)" },
  "FRND":         { color: "#EC4899", bg: "rgba(236,72,153,0.08)",  border: "rgba(236,72,153,0.22)" },
  "Goldman Sachs":{ color: "#5B9BD5", bg: "rgba(91,155,213,0.08)",  border: "rgba(91,155,213,0.22)" }
};

/* ── Reusable logo tile ─────────────────────────────────── */
const CompanyLogo = ({ company, size = 48 }) => {
  const [failed, setFailed] = useState(false);
  const cfg = companyLogos[company];
  const accent = companyAccents[company] || { color: "#71717A", bg: "#141416", border: "#27272A" };

  if (!cfg || failed) {
    return (
      <div
        className="rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size, background: accent.bg, border: `1px solid ${accent.border}` }}
      >
        <span className="text-sm font-bold" style={{ color: accent.color }}>
          {company.slice(0, 2).toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
      style={{
        width: size, height: size,
        background: cfg.bg,
        border: `1px solid ${accent.border}`,
        padding: cfg.padding
      }}
    >
      <img
        src={cfg.url}
        alt={company}
        className={`w-full h-full ${cfg.cover ? "object-cover" : "object-contain"}`}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

/* ── Single-company card (GoodScore / Goldman Sachs) ────── */
const SingleCard = ({ exp, isLast }) => {
  const [expanded, setExpanded] = useState(true);
  const accent = companyAccents[exp.company] || { color: "#71717A" };

  return (
    <div className="relative group">
      {!isLast && (
        <div
          className="absolute left-[22px] top-[72px] bottom-0 w-px pointer-events-none"
          style={{ background: "linear-gradient(to bottom, #2A2A30, transparent)" }}
        />
      )}
      <div
        className="p-5 rounded-xl border transition-all duration-300 group-hover:border-[#2E2E36]"
        style={{ background: "#0F0F12", border: "1px solid #1C1C22" }}
      >
        {/* Header */}
        <div className="flex items-start gap-4">
          <CompanyLogo company={exp.company} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[#F5F5F5] font-semibold text-base leading-tight">{exp.role}</h3>
              {exp.current && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ background: "rgba(16,185,129,0.12)", color: "#10B981", border: "1px solid rgba(16,185,129,0.3)" }}
                >
                  <span className="w-1 h-1 rounded-full bg-[#10B981] animate-pulse" />
                  Current
                </span>
              )}
            </div>
            <p className="font-semibold text-sm mt-0.5" style={{ color: accent.color }}>{exp.company}</p>
          </div>
          {/* Date + location top-right */}
          <div className="flex-shrink-0 text-right hidden sm:flex flex-col items-end gap-1 pt-0.5">
            <span className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: "#6B6B7A" }}>
              <Calendar className="w-3 h-3" />{exp.period}
            </span>
            <span className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: "#6B6B7A" }}>
              <MapPin className="w-3 h-3" />{exp.location}
            </span>
          </div>
        </div>

        {/* Mobile date/loc */}
        <div className="flex items-center gap-4 mt-2 sm:hidden">
          <span className="flex items-center gap-1 text-xs" style={{ color: "#6B6B7A" }}>
            <Calendar className="w-3 h-3" />{exp.period}
          </span>
          <span className="flex items-center gap-1 text-xs" style={{ color: "#6B6B7A" }}>
            <MapPin className="w-3 h-3" />{exp.location}
          </span>
        </div>

        <p className="text-sm leading-relaxed mt-4 mb-3" style={{ color: "#B0B0BE" }}>{exp.description}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {exp.tags.map(tag => (
            <span key={tag} className="px-2.5 py-0.5 rounded-md text-xs font-medium"
              style={{ background: "#1A1A22", color: "#8888A0", border: "1px solid #2A2A34" }}>{tag}</span>
          ))}
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium transition-colors duration-200"
          style={{ color: expanded ? "#06B6D4" : "#52525B" }}
          onMouseEnter={e => e.currentTarget.style.color = "#06B6D4"}
          onMouseLeave={e => e.currentTarget.style.color = expanded ? "#06B6D4" : "#52525B"}
        >
          {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Collapse</> : <><ChevronDown className="w-3.5 h-3.5" /> Key highlights</>}
        </button>

        {expanded && (
          <ul className="mt-4 space-y-2.5 pl-1">
            {exp.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-[7px]" style={{ background: accent.color }} />
                <span className="text-sm leading-relaxed" style={{ color: "#C8C8D8" }}>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/* ── Sub-role row inside grouped card ───────────────────── */
const SubRole = ({ role, accent, isFirst, isLast }) => {
  const [expanded, setExpanded] = useState(isFirst);

  return (
    <div className="relative pl-5">
      {/* Vertical connector line */}
      {!isLast && (
        <div
          className="absolute left-[7px] top-5 bottom-0 w-px"
          style={{ background: "linear-gradient(to bottom, #2A2A30 60%, transparent)" }}
        />
      )}
      {/* Dot marker */}
      <div
        className="absolute left-0 top-[6px] w-3.5 h-3.5 rounded-full border-2 flex-shrink-0"
        style={{
          background: isFirst ? accent.color : "#1C1C22",
          borderColor: isFirst ? accent.color : "#2A2A30"
        }}
      />

      {/* Role header */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[#E8E8E8] font-semibold text-sm">{role.role}</h4>
            {isFirst && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                style={{ background: "rgba(236,72,153,0.1)", color: "#EC4899", border: "1px solid rgba(236,72,153,0.25)" }}
              >
                <TrendingUp className="w-2.5 h-2.5" /> Latest
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: "#6B6B7A" }}>{role.period}</p>
        </div>
      </div>

      <p className="text-xs leading-relaxed mb-2" style={{ color: "#909099" }}>{role.description}</p>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-2">
        {role.tags.map(tag => (
          <span key={tag} className="px-2 py-0.5 rounded text-xs"
            style={{ background: "#1A1A22", color: "#727280", border: "1px solid #252530" }}>{tag}</span>
        ))}
      </div>

      {/* Toggle highlights */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs transition-colors duration-200 mb-4"
        style={{ color: expanded ? "#06B6D4" : "#454555" }}
        onMouseEnter={e => e.currentTarget.style.color = "#06B6D4"}
        onMouseLeave={e => e.currentTarget.style.color = expanded ? "#06B6D4" : "#454555"}
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Collapse</> : <><ChevronDown className="w-3 h-3" /> Key highlights</>}
      </button>

      {expanded && (
        <ul className="mb-4 space-y-2 pl-0.5">
          {role.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="flex-shrink-0 w-1 h-1 rounded-full mt-[7px]" style={{ background: accent.color }} />
              <span className="text-xs leading-relaxed" style={{ color: "#C0C0CE" }}>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ── Grouped multi-role card (FRND) ─────────────────────── */
const GroupedCard = ({ exp, isLast }) => {
  const accent = companyAccents[exp.company] || { color: "#71717A", border: "#27272A" };

  return (
    <div className="relative group">
      {!isLast && (
        <div
          className="absolute left-[22px] top-[72px] bottom-0 w-px pointer-events-none"
          style={{ background: "linear-gradient(to bottom, #2A2A30, transparent)" }}
        />
      )}
      <div
        className="p-5 rounded-xl border transition-all duration-300 group-hover:border-[#2E2E36]"
        style={{ background: "#0F0F12", border: "1px solid #1C1C22" }}
      >
        {/* Company header */}
        <div className="flex items-start gap-4 mb-5">
          <CompanyLogo company={exp.company} />
          <div className="flex-1 min-w-0">
            <h3 className="text-[#F5F5F5] font-bold text-base leading-tight">{exp.company}</h3>
            <p className="text-xs mt-0.5 font-medium" style={{ color: accent.color }}>
              {exp.roles.length} roles · {exp.overallPeriod}
            </p>
          </div>
          {/* Overall period + location top-right */}
          <div className="flex-shrink-0 text-right hidden sm:flex flex-col items-end gap-1 pt-0.5">
            <span className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: "#6B6B7A" }}>
              <Calendar className="w-3 h-3" />{exp.overallPeriod}
            </span>
            <span className="flex items-center gap-1 text-xs whitespace-nowrap" style={{ color: "#6B6B7A" }}>
              <MapPin className="w-3 h-3" />{exp.location}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="mb-4" style={{ height: 1, background: "#1E1E26" }} />

        {/* Sub-roles */}
        <div>
          {exp.roles.map((role, i) => (
            <SubRole
              key={i}
              role={role}
              accent={accent}
              isFirst={i === 0}
              isLast={i === exp.roles.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Section ─────────────────────────────────────────────── */
const ExperienceSection = () => (
  <section id="experience" className="py-16 md:py-24" style={{ background: "#09090B" }}>
    <div className="max-w-6xl mx-auto px-5 sm:px-6">
      <div className="mb-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#06B6D4] mb-3">Career</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white">Work Experience</h2>
        <p className="mt-3 max-w-lg text-sm leading-relaxed" style={{ color: "#6B6B7A" }}>
          From quantitative analytics at Goldman Sachs to scaling startups 14x — a track record of measurable impact.
        </p>
      </div>

      <div className="space-y-4">
        {experiences.map((exp, index) => {
          const isLast = index === experiences.length - 1;
          return exp.type === "multi"
            ? <GroupedCard key={exp.id} exp={exp} isLast={isLast} />
            : <SingleCard  key={exp.id} exp={exp} isLast={isLast} />;
        })}
      </div>
    </div>
  </section>
);

export default ExperienceSection;
